import { round } from "mathjs";
import { sorting_options } from "@liveflight/utilities/sorting";
import { get_markup } from "@liveflight/utilities/flight-offer";
import {
  flight_offer_total_duration,
  flight_offer_average_duration,
} from "@liveflight/utilities/flight-duration";

export function parse_flight_offer_for_ecommerce_item(flightOffer) {
  const brand = `${flightOffer.source}-${flightOffer.itineraries[0].segments[0].carrierCode}`;

  const itemName = flightOffer.itineraries
    .map((itinerary) => {
      return itinerary.segments
        .map((segment) => {
          return `${segment.departure.iataCode}${segment.arrival.iataCode}`;
        })
        .join("-");
    })
    .join("/");

  const price = flightOffer.price.grandTotal;

  const firstSegment = flightOffer.itineraries[0].segments[0];
  const itemId = [
    firstSegment.departure && firstSegment.departure.at
      ? firstSegment.departure.at
      : "",
    firstSegment.aircraft && firstSegment.aircraft.code
      ? firstSegment.aircraft.code
      : "",
    firstSegment.number ? firstSegment.number : "",
  ].join("|");

  return {
    item_id: itemId,
    item_name: itemName,
    item_brand: brand,
    price: price,
    item_category: "",
  };
}

export function parse_flight_offers(flightOffers) {
  const flights = flightOffers.data ? flightOffers.data : [];
  const dictionaries = flightOffers.dictionaries
    ? flightOffers.dictionaries
    : {};
  const meta = flightOffers.meta ? flightOffers.meta : {};
  let best_picks = {};
  if (flightOffers.bestFlight && flightOffers.bestFlight.benchmarks) {
    Object.keys(sorting_options).forEach((sorting) => {
      let flight_offer_id = flightOffers.bestFlight.bestOfferId;
      if (sorting === "price") {
        flight_offer_id = flightOffers.bestFlight.benchmarks.bestPriceId;
      }
      if (sorting === "duration") {
        flight_offer_id = flightOffers.bestFlight.benchmarks.bestDurationId;
      }

      const flight_offer = flights.find(
        (f) => f.id === String(flight_offer_id),
      );
      if (flight_offer) {
        best_picks[sorting] = {
          total_duration: flight_offer_total_duration(flight_offer),
          duration: flight_offer_average_duration(flight_offer),
          flight_offer_id: String(flight_offer_id),
          rankings: flightOffers.bestFlight.metadata || {},
          price: {
            ...flight_offer.price,
            ...get_markup(flight_offer),
          },
        };
      }
    });
  }
  return {
    best_picks,
    flights: flights,
    //offers: offersSorted,
    dictionaries: dictionaries,
    meta: meta,
  };
}

// se un itinerary ha più segmenti gestiti dallo stesso air carrier ne stampo uno solo
export function parse_flight_offers_itinerary_carriers(segments) {
  let carriers = [];
  segments.forEach((segment) => {
    carriers.push(segment.carrierCode);
  });
  return [...new Set(carriers)];
}

export function parse_flight_offers_carriers(flight_offer) {
  let carriers = [];
  flight_offer.itineraries.forEach((i) => {
    let c = parse_flight_offers_itinerary_carriers(i.segments);
    carriers = [...carriers, ...c];
  });
  return [...new Set(carriers)];
}

export function parse_flight_offers_classes(flight_offer) {
  let classes_by_segment = {
    //segment_id: [classe_pax_1, classe_pax_2, ...]
  };

  let cabins = {
    ECONOMY: "Economy",
    PREMIUM_ECONOMY: "Premium economy",
    BUSINESS: "Business",
    FIRST: "First",
  };

  // in caso di schedule changes possono mancare i fareDetailsBySegment dei segmenti modificati
  // quindi preparo classes_by_segment popolando con gli id dei segmenti che trovo negli itinerari
  // così se manca un fareDetailsBySegment non si pianta il rendering
  flight_offer.itineraries.forEach((it) => {
    it.segments.forEach((seg) => {
      classes_by_segment[seg.id] = [];
    });
  });

  flight_offer.travelerPricings.forEach((t) => {
    if (t.fareDetailsBySegment) {
      t.fareDetailsBySegment.forEach((f) => {
        if (!classes_by_segment[f.segmentId])
          classes_by_segment[f.segmentId] = [];
        if (f.cabin) {
          // "traduce" e pusha
          classes_by_segment[f.segmentId].push(
            cabins[f.cabin] ? cabins[f.cabin] : f.cabin,
          );

          // elimina duplicati
          classes_by_segment[f.segmentId] = classes_by_segment[
            f.segmentId
          ].filter((element, index) => {
            return classes_by_segment[f.segmentId].indexOf(element) === index;
          });
        }
      });
    }
  });

  return classes_by_segment;
}

export function formatPassengerName(passenger, fullName = false) {
  if (!passenger) return "";
  return [
    passenger.firstname,
    fullName ? passenger.middlename : undefined,
    passenger.lastname,
    fullName ? passenger.second_lastname : undefined,
  ]
    .filter((e) => !!e)
    .join(" ");
}

export function formatSegmentLabel(segmentInfo) {
  const { arrival, departure } = segmentInfo;
  return departure.iataCode + " › " + arrival.iataCode;
}

export const formatPrice = (price) => {
  const roundedPrice = String(round(price, 2));

  if (!roundedPrice.includes(".")) {
    return roundedPrice + ".00";
  }

  try {
    const decimals = roundedPrice.split(".")[1];
    return roundedPrice + (decimals.length === 1 ? "0" : "");
  } catch (e) {
    return roundedPrice;
  }
};

export const is_infant = (traveler) =>
  !!traveler &&
  ["HELD_INFANT", "SEATED_INFANT"].includes(
    traveler.traveler_type || traveler.travelerType,
  );
export const is_child = (traveler) =>
  !!traveler && (traveler.traveler_type || traveler.travelerType) === "CHILD";
export const is_adult = (traveler) =>
  !!traveler && (traveler.traveler_type || traveler.travelerType) === "ADULT";

const OBJECT_PATH_SEPARATOR = ".";

export const getObjectField = (obj, path, defaultValue) => {
  // Found the value, return it to caller
  if (obj[path]) {
    return obj[path];
  }
  const pathSteps = path.split(OBJECT_PATH_SEPARATOR);
  if (obj[pathSteps[0]]) {
    return getObjectField(
      obj[pathSteps[0]],
      pathSteps.slice(1, pathSteps.length).join(OBJECT_PATH_SEPARATOR),
      defaultValue,
    );
  }

  // If we cannot go deeper go to default
  return defaultValue;
};

export const setObjectField = (obj, path, value) => {
  const pathSteps = path.split(OBJECT_PATH_SEPARATOR);
  if (pathSteps.length > 1) {
    return {
      ...obj,
      [pathSteps[0]]: setObjectField(
        obj[pathSteps[0]] || {},
        pathSteps.slice(1, pathSteps.length).join(OBJECT_PATH_SEPARATOR),
        value,
      ),
    };
  }
  return { ...obj, [path]: value };
};

export const capitalize = (text = "") =>
  String(text).charAt(0).toUpperCase() + String(text).slice(1).toLowerCase();

const CURRENCY_SYMBOLS = {
  EUR: "€",
};

export const currency = (verbose = "") =>
  CURRENCY_SYMBOLS[verbose.toUpperCase()] || verbose;

const MONTHS = {
  0: "gennaio",
  1: "febbraio",
  2: "marzo",
  3: "aprile",
  4: "maggio",
  5: "giugno",
  6: "luglio",
  7: "agosto",
  8: "settembre",
  9: "ottobre",
  10: "novembre",
  11: "dicembre",
};

export const verbose_month = (month_index) => MONTHS[month_index] || "";